import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "store";
import SwitchBtn from "components/buttons/SwitchBtn";
import { useTranslation } from "react-i18next";
import { placeholderFallbacks } from "./PushMainTab";
import styles from "./PushLocalesTab.module.scss";

interface Translation {
  locale: string;
  title: string;
  content: string;
  isActive: number;
}

interface PushLocalesTabProps {
  activeLocale: string;
  setActiveLocale: React.Dispatch<React.SetStateAction<string>>;

  translation: Translation;
  setTranslation: React.Dispatch<React.SetStateAction<Translation>>;
  onInitialTranslationChange: (translation: Translation) => void;
  clearFieldError: (field: string) => void;
  fieldErrors?: Record<string, string>;
  onLoadingChange: (isLoading: boolean) => void;
  readOnly?: boolean;
}

export const PushLocalesTab: React.FC<PushLocalesTabProps> = ({
  activeLocale,
  setActiveLocale,
  translation,
  setTranslation,
  onInitialTranslationChange,
  clearFieldError,
  fieldErrors,
  onLoadingChange,
  readOnly,
}) => {
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");

  const localesList = useAppSelector((state) => state.settings.locales);
  const placeholders = useAppSelector(
    (store) => store["out-notif"].placeholders,
  );

  const { t } = useTranslation();

  const updateLoading = (isLoading: boolean) => {
    setLoading(isLoading);
    onLoadingChange(isLoading);
  };

  useEffect(() => {
    if (localesList.length && !activeLocale) {
      setActiveLocale(localesList[0]);
    }
  }, [localesList, activeLocale]);

  useEffect(() => {
    loadTranslation();
  }, [activeLocale]);

  const loadTranslation = async () => {
    if (!entityId || !activeLocale) {
      return;
    }

    updateLoading(true);

    try {
      const result = await instance.get(
        `${apiRoutes.notificationTranslation}/${entityId}?locale=${activeLocale}`,
      );

      const loadedTranslation = {
        locale: activeLocale,
        title: result.data?.title ?? "",
        content: result.data?.content ?? "",
        isActive: result.data?.isActive ? 1 : 0,
      };
      setTranslation(loadedTranslation);
      onInitialTranslationChange(loadedTranslation);
    } finally {
      updateLoading(false);
    }
  };

  return (
    <div>
      <div className="game-modal-tabs small">
        {localesList.map((loc) => (
          <div
            key={loc}
            className={`tab-item ${activeLocale === loc ? "active" : ""}`}
            onClick={() => {
              if (activeLocale !== loc) {
                updateLoading(true);
                setActiveLocale(loc);
              }
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${loc
                .split("-")[1]
                ?.toLowerCase()}.png`}
              alt=""
            />
            {loc}
          </div>
        ))}
      </div>

      {loading ? (
        <ModalTabSkeleton />
      ) : (
        <div className={styles.content}>
          <div className="notification-locale-header">
            <div className="notification-locale-header__title">
              <InnerInput
                label={t("title")}
                value={translation.title}
                type="text"
                id="notification-locale-title"
                onChange={(value) => {
                  setTranslation((prev) => ({
                    ...prev,
                    title: value,
                  }));
                  clearFieldError("title");
                }}
                hasError={Boolean(fieldErrors?.["title"])}
                disabled={readOnly}
              />
            </div>

            <div className="notification-locale-header__switch">
              <SwitchBtn
                activeTxt={t("active")}
                notActiveTxt={t("hidden")}
                isActive={!!translation.isActive}
                handleClick={() =>
                  setTranslation((prev) => ({
                    ...prev,
                    isActive: prev.isActive ? 0 : 1,
                  }))
                }
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="notification-locale-content mt-10">
            <InnerInput
              label={t("content")}
              value={translation.content}
              type="textarea"
              id="notification-locale-content"
              tooltipText={t("usePlaceholdersInContentNotification")}
              tooltipPosition="right"
              onChange={(value) => {
                setTranslation((prev) => ({
                  ...prev,
                  content: value,
                }));
                clearFieldError("content");
              }}
              hasError={Boolean(fieldErrors?.["content"])}
              disabled={readOnly}
            />
            <div className="out-notif-placeholders out-notif-placeholders--locale">
              {placeholders.map((item) => (
                <div key={item} className="out-notif-placeholders__item">
                  {`{{ ${item} || ${placeholderFallbacks[item] || "value"} }}`}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
